import { memo, useMemo, useCallback } from "react";
import { Virtuoso } from "react-virtuoso";
import { MessageSquare, CheckCheck, Pin } from "lucide-react";
import { getStatusColor, getDayLabel } from "../../utils/helpers";

const ConversationItem = memo(({ conv, setSelectedChat, isActive, pinned }) => {
  const displayName = conv?.contact?.name ?? "";

  const nameToShow = useMemo(() => {
    if (!displayName.endsWith("@c.us")) return displayName;
    return displayName.replace(/\D/g, "");
  }, [displayName]);

  const messages = conv?.messages ?? [];
  const lastMsg = messages.at(-1);

  const unreadCount = conv?._count?.messages ?? 0;
  const users = conv?.users ?? [];
  const analysis = conv?.analyses?.[0];

  const handleClick = useCallback(() => {
    setSelectedChat(conv);
  }, [setSelectedChat, conv]);

  return (
    <div
      onClick={handleClick}
      className={`cursor-pointer border-b border-gray-100 p-4 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/40 ${
        isActive
          ? "border-l-4 border-l-green-500 bg-green-50 dark:bg-green-900/20"
          : ""
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`my-auto flex h-12 w-12 items-center justify-center overflow-hidden rounded-full ${
            isActive ? "ring-2 ring-green-500" : ""
          } bg-green-500 text-white`}
        >
          <img
            src={conv.contact.profilePic || "/default-avatar.png"}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/default-avatar.png";
            }}
            alt="Avatar"
            className="h-full w-full object-cover"
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between">
            <h3 className="truncate font-medium text-gray-900 dark:text-gray-100">
              {nameToShow || "Sem nome"}
            </h3>

            <span className="flex flex-shrink-0 gap-2 text-xs text-gray-500 dark:text-gray-400">
              {pinned && <Pin className="h-3 w-3 text-yellow-500" />}
              {conv.lastMessageAt
                ? new Date(conv.lastMessageAt).toLocaleTimeString("pt-BR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : ""}
            </span>
          </div>

          <div className="mt-1 flex min-w-0 items-center gap-2">
            <p className="flex min-w-0 flex-1 items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
              {messages.length ? (
                <>
                  {lastMsg?.direction === "OUTGOING" && (
                    <CheckCheck
                      className={`h-4 w-4 flex-shrink-0 ${
                        lastMsg?.isRead ? "text-blue-600" : "text-gray-400"
                      }`}
                    />
                  )}

                  <span className="truncate">{lastMsg?.messageBody ?? ""}</span>
                </>
              ) : (
                <span className="truncate">Nenhuma mensagem</span>
              )}
            </p>

            {unreadCount > 0 && (
              <span className="flex-shrink-0 rounded-full bg-green-500 px-2 py-0.5 text-xs font-bold text-white">
                {unreadCount}
              </span>
            )}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span
              className={`rounded-full px-2 py-0.5 ${getStatusColor(
                conv.status,
              )}`}
            >
              {conv.status}
            </span>

            {analysis && (
              <span className={`rounded-full px-2 py-0.5 font-semibold ${
                ["alto", "critico"].includes(analysis.riskLevel)
                  ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                  : analysis.riskLevel === "medio"
                    ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300"
                    : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
              }`}>
                IA: risco {analysis.riskLevel}
              </span>
            )}

            {!analysis && conv.analysisPending && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                IA: pendente
              </span>
            )}

            {conv.tags?.slice(0, 3).map((tag) => (
              <span
                key={tag.id}
                className="rounded-full px-2 py-0.5"
                style={{
                  backgroundColor: tag.backgroundColor,
                  color: tag.textColor,
                }}
              >
                {tag.name}
              </span>
            ))}

            {users.length > 0 && (
              <span className="truncate text-gray-500 dark:text-gray-400">
                {users.map((u) => u.name).join(", ")}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

const ConversationsList = memo(
  ({ conversations, pinnedIds, selectedChat, setSelectedChat, onLoadMore }) => {
    const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);

    const items = useMemo(() => {
      const groups = { Fixadas: [] };

      for (const conv of conversations) {
        if (pinnedSet.has(conv.id)) {
          groups.Fixadas.push(conv);
        } else {
          const day = getDayLabel(conv.lastMessageAt);
          if (!groups[day]) groups[day] = [];
          groups[day].push(conv);
        }
      }

      const list = [];

      for (const [label, convs] of Object.entries(groups)) {
        if (!convs.length) continue;

        list.push({ type: "header", label });

        for (const conv of convs) {
          list.push({ type: "conversation", conv });
        }
      }

      return list;
    }, [conversations, pinnedSet]);

    if (!conversations.length) {
      return (
        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
          <MessageSquare className="mx-auto mb-2 h-8 w-8 text-gray-300 dark:text-gray-600" />
          <p className="text-sm">Nenhuma conversa encontrada</p>
        </div>
      );
    }

    return (
      <Virtuoso
        className="flex-1"
        data={items}
        increaseViewportBy={500}
        endReached={onLoadMore}
        itemContent={(index, item) => {
          if (item.type === "header") {
            return (
              <div
                className={`px-4 py-2 text-xs font-semibold ${
                  item.label === "Fixadas"
                    ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
                    : "bg-gray-100 text-gray-600 dark:bg-gray-900 dark:text-gray-400"
                }`}
              >
                {item.label}
              </div>
            );
          }

          const conv = item.conv;

          return (
            <ConversationItem
              conv={conv}
              setSelectedChat={setSelectedChat}
              isActive={selectedChat?.id === conv.id}
              pinned={pinnedSet.has(conv.id)}
            />
          );
        }}
      />
    );
  },
);

export default ConversationsList;
